<template>
  <v-container :class="$vuetify.display.xs ? 'documentBottomSpacingMobile' : ''" class="pl-0 pr-0">
    <!-- header -->
    <v-row class="main-body pt-2 pb-4" align-content="center">
      <v-col cols="auto" align-self="center" class="d-flex">
        <v-icon class="mr-2" size="34" color="#b4c9db" :icon="mdiFolderOutline" />
        <h2 class="text-weight-regular text-white" role="heading" aria-level="1">
          {{ metadata.title }}
        </h2>
      </v-col>
    </v-row>
    <v-card class="mb-10 mb-sm-4 mb-sm-4 mb-md-4 mb-lg-4 mb-xl-4 mb-xxl-4">
      <v-row v-if="routeName !== RouteNames.NOTICES_AND_CONFIRMATIONS">
        <v-col cols="12">
          <v-card tile flat class="pa-2" id="documents-note-text">
            <!-- Header Text - Investments -->
            <v-card-text v-if="isCanadian && routeName === RouteNames.INVESTMENT_STATEMENTS">
              <!-- View Documents for Last... -->
              <p class="text-body-1">{{ $t('L_view_statements_for') }}</p>
              <OnexSelect
                class="pb-0"
                max-width="200"
                v-model="viewInvestmentStatementsFilter"
                :items="viewInvestmentStatementsFilterItems"
                :label="$t('')"
                :menu-props="{ location: 'bottom center', scrollStrategy: 'close' }"
              />
              <p class="text-body-1">{{ $t('L_please_note_that_some_documents') }}</p>
            </v-card-text>
            <!-- Header Text - Policies -->
            <v-card-text v-else>
              <!-- Please note that some documents... -->
              {{ $t('L_please_note_that_some_documents') }}
              <p class="mt-4 font-weight-bold">
                {{ $t('L_if_you_do_not_see_a_copy_of_your_full_life_policy_it') }}
              </p>
            </v-card-text>
            <v-card-text
              v-if="isCanadian && routeName === RouteNames.INVESTMENT_STATEMENTS"
              class="font-weight-bold"
            >
              {{
                $t(
                  'L_only_mutual_fund_account_statements_are_available_to_view_on_myprimerica_for_common_sense'
                )
              }}
              <a href="javascript:void(0)" @click="agfLink()">www.agf.com</a>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" v-if="routeName === RouteNames.NOTICES_AND_CONFIRMATIONS">
          <!-- Header Text - Notices -->
          <v-card tile flat class="pa-2" id="documents-note-text">
            <v-card-text id="please-note-verbiage">
              <p class="text-body-1">{{ $t('L_view_documents_for_last') }}</p>
              <OnexSelect
                class="py-1"
                max-width="200"
                v-model="viewNoticesAndConfirmationsFilter"
                :items="viewNoticesAndConfirmationsFilterItems"
                :label="$t('')"
                :menu-props="{ location: 'bottom center', scrollStrategy: 'close' }"
              />
              <p class="text-body-1">{{ $t('L_please_note_that_some_documents') }}</p>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12">
          <v-card v-if="metadata?.documentList?.length > 0">
            <v-list lines="two">
              <template
                v-for="(item, index) in metadata?.documentList"
                :key="item.name + '' + index"
              >
                <v-list-item avatar ripple @click="getDocumentPDF(item)">
                  <v-list-item-title id="document-title">
                    {{ item.name }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="subtitle-color">
                    <v-row>
                      <v-col cols="6">{{ item.date }}</v-col>
                      <v-col cols="6">{{ item.type }}</v-col>
                    </v-row>
                  </v-list-item-subtitle>
                  <template v-slot:append="">
                    <v-list-item-action>
                      <v-icon
                        id="right-arrow"
                        color="primary"
                        size="large"
                        :icon="mdiChevronRight"
                      />
                    </v-list-item-action>
                  </template>
                </v-list-item>
                <v-divider v-if="index + 1 < metadata?.documentList?.length" :key="index" />
              </template>
            </v-list>
          </v-card>
          <v-card v-else>
            <v-card-text>
              <div>
                <v-img
                  id="documents-folder-img"
                  height="180"
                  width="180"
                  src="@/assets/documentsFolder.png"
                  alt="documents-folder"
                  class="d-flex mx-auto"
                />
              </div>
              <div id="you-have-no-available" class="font-weight-bold text-center">
                {{ $t('L_no_documents_currently_available') }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-card>
  </v-container>
</template>

<script lang="ts">
import { openInNewWindow } from '@/mixins/openNewWindow';
import { mapActions, mapState, mapStores } from 'pinia';
import { useGlobalStore } from '@/stores/global/global.store';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { defineComponent, type ComponentOptions } from 'vue';
import { COUNTRIES } from '@/shared/models/constants.models';
import { CapacitorFacade } from '@/plugins/capacitorPlugin';
import { formatDate, parseISO, isAfter, isEqual } from 'date-fns';
import { mdiChevronRight, mdiFolderOutline } from '@mdi/js';
import { RouteNames } from '@/router/routeNames';
import { OnexSelect } from '@primerica/onex-ui-lib';
import type {
  IGlobalStoreDocumentsInvestmentStatementItem,
  IGlobalStoreDocumentsItem,
} from '@/stores/global/globalStore.model';
import type { IDocumentMetadata } from '@/shared/api/models.content';

const componenOptions: ComponentOptions = {
  name: 'DocumentList',
  components: {
    OnexSelect,
  },
  mixins: [openInNewWindow],
  props: {
    routeName: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      documentsLoaded: true,
      loading: true,
      viewNoticesAndConfirmationsFilter: 30,
      viewInvestmentStatementsFilter: new Date().getFullYear(),
      mdiFolderOutline,
      mdiChevronRight,
      RouteNames,
    };
  },
  computed: {
    ...mapStores(useGlobalStore, usePolicyStore),
    ...mapState(useGlobalStore, {
      dateFormat: 'getDateFormat',
      currentLocale: 'getLocale',
      documents: 'documents',
      policyDocuments: 'policyDocuments',
      noticesAndConfirmations: 'noticesAndConfirmations',
      investmentStatements: 'investmentStatements',
      base64String: 'base64String',
    }),

    ...mapState(useCustomerStore, ['binaryInvestmentStatement', 'customerProfile']),
    isCanadian() {
      return this.customerProfile?.country === COUNTRIES.CANADA;
    },
    isMobile() {
      return this.$vuetify.display.xs;
    },
    viewNoticesAndConfirmationsFilterItems() {
      return [
        { title: this.$t('L_1_month'), value: 30 },
        { title: this.$t('L_3_months'), value: 90 },
        { title: this.$t('L_6_months'), value: 180 },
        { title: this.$t('L_all'), value: 0 },
      ];
    },
    viewInvestmentStatementsFilterItems() {
      const currentYear = new Date().getFullYear();
      const items = [{ title: currentYear.toString(), value: currentYear }];
      for (let yr = 1; yr < 7; yr++) {
        items.push({ title: currentYear - yr + '', value: currentYear - yr });
      }
      items.push({ title: this.$t('L_all'), value: 0 });
      return items;
    },
    filteredNoticesAndConfirmations() {
      const cutoffDate = new Date(
        Date.now() - +this.viewNoticesAndConfirmationsFilter * 24 * 60 * 60 * 1000
      );
      const filteredDocumentsList = this.globalStore.documents?.notices?.filter(
        (doc: IGlobalStoreDocumentsItem) => {
          const isWithinCutoff =
            doc.dateReceived &&
            (isAfter(parseISO(doc.dateReceived), cutoffDate) ||
              isEqual(parseISO(doc.dateReceived), cutoffDate));
          return isWithinCutoff;
        }
      );
      return this.viewNoticesAndConfirmationsFilter === 0
        ? this.noticesAndConfirmations
        : filteredDocumentsList;
    },
    filteredInvestmentStatements() {
      const cutoffDate = new Date(this.viewInvestmentStatementsFilter, 0, 1);
      const filteredDocumentsList = this.investmentStatements.filter(
        (doc: IGlobalStoreDocumentsInvestmentStatementItem) => {
          const isWithinCutoff =
            isEqual(parseISO(doc.dateReceived ?? ''), cutoffDate) ||
            parseISO(doc.dateReceived ?? '').getFullYear() === cutoffDate.getFullYear();
          return isWithinCutoff;
        }
      );
      return this.viewInvestmentStatementsFilter === 0
        ? this.investmentStatements
        : filteredDocumentsList;
    },
    metadata() {
      const m: {
        title?: string;
        documentList?: Array<{
          date: string;
          name: string;
          type?: string;
          doc: IGlobalStoreDocumentsItem | IGlobalStoreDocumentsInvestmentStatementItem;
        }>;
      } = {};
      if (this.routeName === RouteNames.POLICY_DOCUMENTS) {
        m.title = this.$t('L_policy_documents');
        const d: IGlobalStoreDocumentsItem[] = this.policyDocuments;
        const r = d.map((i: IGlobalStoreDocumentsItem) => {
          const _n = i.labels
            ? i.labels.find((label) => label.language === this.currentLocale)
            : null;
          const _i = {
            date: i.dateReceived ? formatDate(parseISO(i.dateReceived), this.dateFormat) : '',
            name: `${_n?.name} - ${i.contract}`,
            type: _n?.type,
            doc: i,
          };
          return _i;
        });
        m.documentList = r;
      } else if (this.routeName === RouteNames.NOTICES_AND_CONFIRMATIONS) {
        m.title = this.$t('L_notices_and_confirmations');
        const d: IGlobalStoreDocumentsItem[] = this.filteredNoticesAndConfirmations; // this is a filtered this.noticesAndConfirmations;
        const r = d.map((i: IGlobalStoreDocumentsItem) => {
          const _n = i.labels
            ? i.labels.find((label) => label.language === this.currentLocale)
            : null;
          const _i = {
            date: i.dateReceived ? formatDate(parseISO(i.dateReceived), this.dateFormat) : '',
            name: `${_n?.name} - ${i.contract}`,
            type: _n?.type,
            doc: i,
          };
          return _i;
        });
        m.documentList = r;
      } else if (this.routeName === RouteNames.INVESTMENT_STATEMENTS) {
        m.title = this.$t('L_investment_statements');
        const d: IGlobalStoreDocumentsInvestmentStatementItem[] = this.filteredInvestmentStatements;
        const r = d.map((i: IGlobalStoreDocumentsInvestmentStatementItem) => {
          const startMonth = i.name ? this.$t(`L_${i.name.period.start.month.toLowerCase()}`) : '';
          const endMonth = i.name ? this.$t(`L_${i.name.period.end.month.toLowerCase()}`) : '';
          const name = i.name
            ? `${startMonth} ${i.name.period.start.day} - ${endMonth} ${i.name.period.end.day}, ${i.name.period.year}`
            : '';
          return {
            date: i.dateReceived ? formatDate(parseISO(i.dateReceived), this.dateFormat) : '',
            name,
            type: `${this.$t(`L_${i.type}`)}`,
            doc: i,
          };
        });
        m.documentList = r;
      }
      return m;
    },
    marginBottomMobile() {
      return {
        'mb-12': !CapacitorFacade.isWeb(),
      };
    },
  },
  created() {},
  watch: {
    isMobile(newVal, oldVal) {
      if (newVal !== oldVal) {
        if (!this.isMobile && this.$route.name === RouteNames.DOCUMENT_LIST_MOBILE) {
          // redirect to mobile view;
          this.$router.push({ name: this.routeName || RouteNames.NOTICES_AND_CONFIRMATIONS });
        }
      }
    },
  },
  methods: {
    ...mapActions(useCustomerStore, ['openStatement']),
    // ...mapActions(['getDocuments']),
    async getDocumentPDF(item: {
      date: string;
      name: string;
      type?: string;
      doc: IGlobalStoreDocumentsItem | IGlobalStoreDocumentsInvestmentStatementItem;
    }) {
      try {
        const t = `view_doc_${item.name[0].toLowerCase()}${item.name.slice(1).replace(/ /g, '')}`;
        const i = this.$dynatrace.enterAction(t);
        this.$dynatrace.leaveAction(i);
      } catch (_error) {
        /* empty */
      }

      if (
        this.routeName === RouteNames.POLICY_DOCUMENTS ||
        this.routeName === RouteNames.NOTICES_AND_CONFIRMATIONS
      ) {
        let url;
        if (item.doc.documentType === 'A') {
          url = await this.getAllDocumentsUrl(item.doc.metadata);
        } else {
          url = await this.getSingleDocumentUrl(item.doc.metadata);
        }
        const isWeb = CapacitorFacade.isWeb();
        await this.openInNewWindow({ url, inappbrowser: !isWeb });
      } else if (this.routeName === RouteNames.INVESTMENT_STATEMENTS) {
        const isWeb = CapacitorFacade.isWeb();
        const url = await this.getInvestmentDocument(item.doc.metadata);
        await this.openInNewWindow({ url, inappbrowser: !isWeb });
      }
    },
    async agfLink() {
      await this.openInNewWindow({ url: 'http://www.agf.com/', target: '_blank' });
    },
    // ***
    getInvestmentDocument(requestObject: IDocumentMetadata) {
      const isWeb = CapacitorFacade.isWeb();
      if (isWeb) {
        return this.openStatement(requestObject).then(() =>
          URL.createObjectURL(
            new Blob([this.binaryInvestmentStatement as BlobPart], { type: 'application/pdf' })
          )
        );
      } else {
        return this.openStatement(requestObject).then(() => this.binaryInvestmentStatement);
      }
    },
    // ***
    getSingleDocumentUrl(requestObject: IDocumentMetadata) {
      const isWeb = CapacitorFacade.isWeb();
      if (isWeb) {
        return this.globalStore
          .getOnePolicyDocument(requestObject)
          .then(() =>
            URL.createObjectURL(new Blob([this.base64String], { type: 'application/pdf' }))
          );
      } else {
        return this.globalStore.getOnePolicyDocument(requestObject).then(() => this.base64String);
      }
    },
    // ***
    getAllDocumentsUrl(requestObject: IDocumentMetadata) {
      const isWeb = CapacitorFacade.isWeb();
      if (isWeb) {
        return this.globalStore
          .getAllPolicyDocuments(requestObject)
          .then(() =>
            URL.createObjectURL(new Blob([this.base64String], { type: 'application/pdf' }))
          );
      } else {
        return this.globalStore.getAllPolicyDocuments(requestObject).then(() => this.base64String);
      }
    },
  },
  beforeRouteEnter(_, __, next) {
    // called before the route that renders this component is confirmed.
    // does NOT have access to `this` component instance,
    // because it has not been created yet when this guard is called!
    next((vm) => {
      // access to component public instance via `vm`
      try {
        const i = vm.$dynatrace.enterAction('view_documentsPage');
        vm.$dynatrace.leaveAction(i);
      } catch (_error) {
        /* empty */
      }
    });
  },
  beforeRouteUpdate() {
    // called when the route that renders this component has changed,
    // but this component is reused in the new route.
    try {
      const i = this.$dynatrace.enterAction('view_documentsPage');
      this.$dynatrace.leaveAction(i);
    } catch (_error) {
      /* empty */
    }
  },
};

export default defineComponent(componenOptions);
</script>

<style lang="scss" scoped>
.documentBottomSpacingMobile {
  margin-bottom: 140px;
}
#document-title {
  font-size: 14px !important;
  font-weight: 700 !important;
  color: #02579e;
  cursor: pointer;
}

.subtitle-color {
  font-size: 13px !important;
  color: #666666 !important;
}

.link {
  font-weight: 600;
  color: #0c6dca;
  cursor: pointer;
}

.myNotifications {
  padding-left: 16px;
  padding-right: 16px;
}

.bgAllOfYourAvailable {
  background: #f9f9f9;
}

.contactYourRepButtonWidth {
  width: 264px;
  max-width: 265px;
  min-width: 264px;
}

.picture,
.agentImage,
.icon {
  width: 55px;
  height: 55px;
  margin: 0 auto;
  border-radius: 50%;
}

.rep {
  padding: 15px 0;
  background: #eee;
}

.button-wrapper .v-btn {
  width: 85%;
  margin: 0 auto;
  background-color: #063562 !important;
}

.backgroundColorBlue {
  background-color: #f5f7fa;
}

.backgroundColorWhite {
  background-color: #ffffff;
}

.borderLeftWall {
  border-left: 1px solid #ccc;
}

.paddingForNoDismiss {
  padding-right: 32px;
}

#you-have-no-available {
  line-height: 1.5;
  text-align: center;
  color: #333;
}

#documents-folder-img {
  margin-top: 18px;
  margin-bottom: 18px;
}

#documents-note-text {
  border-bottom: solid 1px #ccc;
  padding: 0px !important;
}
</style>
